/**
 * ArTableProvider
 *
 * Wraps every ArTable descendant with the three values the table shares: the UI
 * state proxy `ArTableShell` created above `StoreProvider`, the row actions, and
 * the ONE clipboard instance (SYD-2314) — the row menus mount two per row, so
 * building it here is what keeps an N-row page at one instance rather than 2N.
 *
 * `disabled` mirrors `ArTableBody`'s own `effectiveDisabled`, PDF export
 * included, so the copy/paste handlers refuse exactly what the body refuses.
 * The store reads are non-reactive on purpose: `useArTableClipboard` is
 * zero-subscription, and a `useSnapshot` here would re-render the whole table on
 * every cell write.
 */

import { FC, ReactNode, useEffect } from 'react';
import { useArTableActions } from '../actions/useArTableActions';
import { useArTableClipboard } from '../clipboard';
import { usePdfExportMode } from '../usePdfExportMode';
import { useStoreNonReactive } from '@/store';
import type { CollectionState, DocumentState } from '@/store';
import type { ArRowData, ArTableType } from '#types/System.Artifacts.types';
import type { ArTableUIState } from '../state/createArTableState';
import { ArTableActionsContext } from './ArTableActionsContext';
import { ArTableClipboardContext } from './ArTableClipboardContext';
import { ArTableUIStateContext } from './ArTableUIStateContext';

type ArTableProviderProps = {
  uiState: ArTableUIState;
  /** The shell's `disabled`; PDF export is folded in here, not at the call site. */
  disabled: boolean;
  children: ReactNode;
};

const ArTableProvider: FC<ArTableProviderProps> = ({ uiState, disabled, children }) => {
  const actions = useArTableActions(uiState);
  const isPdfExportMode = usePdfExportMode();
  const { state: schemaStateRaw } = useStoreNonReactive('schema');
  const { state: rowsStateRaw } = useStoreNonReactive('rows');

  const effectiveDisabled = disabled || isPdfExportMode;

  const clipboard = useArTableClipboard({
    uiState,
    schemaState: schemaStateRaw as DocumentState<ArTableType>,
    rowsState: rowsStateRaw as CollectionState<ArRowData>,
    actions,
    disabled: effectiveDisabled,
  });

  /* Cleanup state when component unmounts */
  useEffect(() => {
    return () => {
      uiState.cellEditing.editingCellsByKey = {};
      uiState.bulkEditPreview.byColumnId = {};
      uiState.bulkEditPreview.pendingConfirmation = null;
    };
  }, [uiState]);

  return (
    <ArTableActionsContext.Provider value={actions}>
      <ArTableClipboardContext.Provider value={clipboard}>
        <ArTableUIStateContext.Provider value={uiState}>
          {children}
        </ArTableUIStateContext.Provider>
      </ArTableClipboardContext.Provider>
    </ArTableActionsContext.Provider>
  );
};

export { ArTableProvider };
